import { formatSecondToMMss } from '@doltech/utils/lib/date';
import cl from 'classnames';
import { isNil, throttle } from 'lodash';
import * as React from 'react';
import ReactPlayer, { FileConfig } from 'react-player/file';
import ReactSlider from 'react-slider';
import styled from 'styled-components';

import { figmaColors } from '../../colors';
import { colorsV2 } from '../../colors-v2';
import { Div, renderNode } from '../../renderNode';

const audioFetchConfig: FileConfig = {
  forceAudio: true,
  attributes: {
    controls: true,
    crossOrigin: 'anonymous',
    preload: 'metadata',
  },
};

export interface AudioPlayerProps extends React.HTMLAttributes<HTMLDivElement> {
  url: string;
  onEnded?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onAudioProgress?: (timeInSeconds: number) => void;
  volume?: number;

  startTimeInSeconds?: number;
  endTimeInSeconds?: number;
  showThumb?: boolean;
  interactive?: boolean;
  autoReplay?: boolean;
  autoReplayDelay?: number;
  autoplay?: boolean;
  playbackRate?: number;
  testTimeTakenInSeconds?: number;
  testDurationInSeconds?: number;
  customSlider?: boolean;
  /**
   * Example: We have a audio have 40 second length
   * full: display full 40s in slider (default mode)
   * segment:
   * (1)if startTimeInSeconds and not endTimeInSeconds
   *      then only show from startTimeInSeconds to end of audio
   * (2)if endTimeInSeconds and not startTimeInSeconds
   *      then only show from 0 to endTimeInSeconds
   * (3)if both startTimeInSeconds and endTimeInSeconds
   *      then only the segment
   *
   * Example:
   *  (1) startTimeInSeconds = 5
   *    show 35s as full slider length
   *  (2) endTimeInSeconds = 20
   *    show 20s as full slider length
   *  (3) startTimeInSeconds = 5 and endTimeInSeconds=20
   *    show 15s as full slider length
   *
   */
  displayMode?: 'full' | 'segment';
  thumbType?: 'duration' | 'circle';
  renderDuration?: ({ progressInSeconds, durationInSeconds }) => JSX.Element;
}

export interface FlatPlayerInstance {
  playPause(): void;
  forward15Second(): void;
  rewind15Second(): void;
  startPlaying(): void;
  pause(): void;
  play(): void;
  replay(): void;
}

export const FlatAudioPlayer = React.forwardRef((props: AudioPlayerProps, ref) => {
  const {
    url,
    title,
    onEnded,
    volume = 1,
    onPlay,
    onPause,
    onAudioProgress,
    startTimeInSeconds,
    endTimeInSeconds,
    showThumb = true,
    thumbType = 'duration',
    interactive,
    autoReplay = false,
    playbackRate = 1,
    autoReplayDelay = 2000,
    displayMode = 'full',
    autoplay = true,
    renderDuration,
    testTimeTakenInSeconds,
    testDurationInSeconds,
    customSlider,
    ...rest
  } = props;
  const [progressInSeconds, setProgressInSeconds] = React.useState(0);
  const [durationInSeconds, setDurationInSeconds] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const audioPlayer = React.useRef<any>(null);

  const onProgress = React.useCallback(
    (state: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => {
      const { playedSeconds } = state;
      if (displayMode === 'segment' && !isNil(startTimeInSeconds)) {
        setProgressInSeconds(playedSeconds - startTimeInSeconds);
      } else {
        setProgressInSeconds(playedSeconds);
      }
      if (isNil(endTimeInSeconds) === false && playedSeconds > endTimeInSeconds) {
        setPlaying(false);

        if (isNil(startTimeInSeconds)) {
          audioPlayer.current.seekTo(0, 'seconds');
        } else {
          audioPlayer.current.seekTo(startTimeInSeconds, 'seconds');
        }

        if (autoReplay) {
          setTimeout(() => {
            setPlaying(true);
          }, autoReplayDelay);
        }
        return;
      }
      if (onAudioProgress) {
        onAudioProgress(playedSeconds);
      }
    },
    [
      endTimeInSeconds,
      onAudioProgress,
      startTimeInSeconds,
      autoReplay,
      autoReplayDelay,
      displayMode,
    ]
  );

  const updateSlider = React.useMemo(() => {
    return throttle((v) => {
      setProgressInSeconds(v);
    }, 1);
  }, []);

  React.useEffect(() => {
    if (isNil(startTimeInSeconds) === false && durationInSeconds > 0) {
      audioPlayer.current.seekTo(startTimeInSeconds, 'seconds');
      if (displayMode === 'full') {
        setProgressInSeconds(startTimeInSeconds);
      }
      if (autoplay) {
        setPlaying(true);
      }
    }
  }, [durationInSeconds, startTimeInSeconds, autoplay, displayMode]);

  const playPause = React.useCallback(() => {
    setPlaying((o) => !o);
  }, []);

  const pause = React.useCallback(() => {
    setPlaying(false);
  }, []);

  const play = React.useCallback(() => {
    setPlaying(true);
    audioPlayer.current.seekTo(audioPlayer.current.getCurrentTime());
  }, []);

  const startPlaying = React.useCallback(() => {
    setPlaying(true);
    if (isNil(startTimeInSeconds)) {
      audioPlayer.current.seekTo(0, 'seconds');
      setProgressInSeconds(0);
    } else {
      audioPlayer.current.seekTo(startTimeInSeconds, 'seconds');
      setProgressInSeconds(startTimeInSeconds);
    }
  }, [startTimeInSeconds]);

  const seekTo = React.useCallback((timeInSeconds) => {
    audioPlayer.current.seekTo(timeInSeconds, 'seconds');
  }, []);

  const forward15Second = React.useCallback(() => {
    if (audioPlayer.current) {
      if (progressInSeconds + 15 > durationInSeconds) {
        audioPlayer.current.seekTo(durationInSeconds, 'seconds');
        setPlaying(false);
        setProgressInSeconds(durationInSeconds);
        return;
      }
      audioPlayer.current.seekTo(progressInSeconds + 15, 'seconds');
      setProgressInSeconds(progressInSeconds + 15);
    }
  }, [progressInSeconds, durationInSeconds]);

  const rewind15Second = React.useCallback(() => {
    if (audioPlayer.current) {
      if (progressInSeconds < 15) {
        audioPlayer.current.seekTo(0, 'seconds');
        setPlaying(false);
        setProgressInSeconds(0);
        return;
      }
      audioPlayer.current.seekTo(progressInSeconds - 15, 'seconds');
      setProgressInSeconds(progressInSeconds - 15);
    }
  }, [progressInSeconds]);

  const replay = React.useCallback(() => {
    if (displayMode === 'full') {
      if (isNil(startTimeInSeconds)) {
        audioPlayer.current.seekTo(0, 'seconds');
        setProgressInSeconds(0);
      } else {
        audioPlayer.current.seekTo(startTimeInSeconds, 'seconds');
        setProgressInSeconds(startTimeInSeconds);
      }
    }

    if (displayMode === 'segment') {
      if (!isNil(endTimeInSeconds) && isNil(startTimeInSeconds)) {
        audioPlayer.current.seekTo(0, 'seconds');
        setProgressInSeconds(0);
      }
      if (!isNil(startTimeInSeconds)) {
        audioPlayer.current.seekTo(startTimeInSeconds, 'seconds');
        setProgressInSeconds(0);
      }
    }
  }, [startTimeInSeconds, displayMode, endTimeInSeconds]);

  const renderThumb = React.useCallback(
    (renderProps, state) => {
      if (showThumb) {
        if (thumbType === 'circle') {
          return (
            <div {...renderProps}>
              <div className="thumb-circle" />
            </div>
          );
        }
        return <div {...renderProps}>{formatSecondToMMss(state.valueNow)}</div>;
      }
      return <div {...renderProps} />;
    },
    [showThumb, thumbType]
  );

  React.useImperativeHandle(ref, () => {
    return {
      ...audioPlayer.current,
      playPause,
      seekTo,
      forward15Second,
      rewind15Second,
      startPlaying,
      pause,
      play,
      replay,
    };
  });

  return (
    <>
      <Container {...rest}>
        <ReactPlayer
          autoplay={autoplay}
          config={audioFetchConfig}
          playbackRate={playbackRate}
          playing={playing}
          playsinline
          className="hidden"
          ref={audioPlayer}
          muted={volume === 0}
          volume={volume}
          url={[
            {
              src: url,
              type: 'audio/mpeg',
            },
          ]}
          onProgress={onProgress}
          onDuration={(duration) => {
            if (displayMode === 'segment') {
              if (!isNil(startTimeInSeconds) && isNil(endTimeInSeconds)) {
                setDurationInSeconds(Math.round(duration - startTimeInSeconds));
              } else if (!isNil(endTimeInSeconds) && isNil(startTimeInSeconds)) {
                setDurationInSeconds(Math.round(endTimeInSeconds));
              } else if (!isNil(endTimeInSeconds) && !isNil(startTimeInSeconds)) {
                setDurationInSeconds(Math.round(endTimeInSeconds - startTimeInSeconds));
              } else {
                setDurationInSeconds(duration);
              }
            } else {
              // mode: full
              setDurationInSeconds(duration);
            }
          }}
          onEnded={onEnded}
          onPlay={onPlay}
          onPause={onPause}
        />

        <ReactSlider
          className="slider-container"
          thumbClassName={cl('thumb', { 'mode-circle': thumbType === 'circle' })}
          trackClassName="track"
          defaultValue={0}
          value={customSlider ? testTimeTakenInSeconds : progressInSeconds}
          max={customSlider ? testDurationInSeconds : durationInSeconds}
          ariaLabel={['Lowest thumb', 'Middle thumb', 'Top thumb']}
          renderThumb={renderThumb}
          pearling
          onChange={(v) => {
            updateSlider(v);
          }}
          onAfterChange={(value) => {
            audioPlayer.current.seekTo(
              (displayMode === 'segment' && startTimeInSeconds ? startTimeInSeconds : 0) + value,
              'seconds'
            );
          }}
          disabled={!interactive}
          step={0.1}
        />
      </Container>
      {renderNode(Div, renderDuration, { progressInSeconds, durationInSeconds })}
    </>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  .hidden {
    display: none;
  }

  .slider-container {
    width: 100%;
    height: 20px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    cursor: pointer;
    .track {
      height: 2px;
      transition: all 0.05s ease;
      &.track-0 {
        background-color: ${colorsV2.primary100};
      }
      &.track-1 {
        background-color: ${colorsV2.gray20};
      }
    }
  }

  .thumb {
    user-select: none;
    font-size: 10px;
    font-weight: 600;
    color: ${colorsV2.white100};
    background: ${colorsV2.primary100};
    padding: 0 4px;
    border-radius: 12px;
    transition: all 0.05s ease;
    line-height: 14px;

    &.mode-circle {
      padding: 0;
      border-radius: 0;
      background-color: transparent;
    }
    .thumb-circle {
      background: ${colorsV2.white100};
      border: 2px solid ${colorsV2.primary80};
      width: 14px;
      height: 14px;
      border-radius: 50%;
    }
  }

  .slider {
    transition: all 0.05s ease;
    width: 100%;
    cursor: move;
    height: 100%;
    transform-origin: 0% 50%;
    background-color: ${figmaColors.primary5};
  }
`;
